<template>
  <div v-if="estimatedCreditResponse" class="print-return-confirmation-container">
    <div class="print-only">
      <!-- BRAND LOGO -->
      <BrandLogo :show-logo="isUPSReturnMethod || isFedExReturnMethod" class-name="print-header" />
      <!-- RETURN INSTRUCTION -->
      <div class="return-confirmation-mrl">
        <ReturnInstructions
          is-print-label
          :uop-flow="!returnDetails.isRmaReturn"
          :return-drop-off-method="returnDetails.returnDropOffMethod"
          :refund-method="returnDetails.refundMethod"
          :is-mkp-order="returnDetails.isMkpOrder"
          :drop-off-date="returnDetails.dropOffByDate || returnDetails.dropOffDate"
          :return-resolution="ssreType"
          :is-replace-exchange="!!ssreType"
          :items="returnItems"
          :page="page"
        />
        <!-- HAZMAT IMAGE -->
        <div v-if="hazmatItems.length">
          <div class="split-label margin-top-xs"></div>
          <h3 class="h3">Hazmat Label</h3>
          <div
            class="hazmat-image"
            :class="{
              'hazmat-packed-with-image-size-safari': isSafari && hasPackedWithItem,
              'hazmat-image-size-safari': isSafari && !hasPackedWithItem,
            }"
          >
            <img class="hazmat-label print-label-load-image" :src="getHazmatImagePath(hazmatLabel)" :alt="`hazmat ${hazmatLabel} battery`" />
            <img class="print-label-load-image" :src="lithiumBatteryForbiddenImg" alt="battery forbidden text" />
          </div>
          <div class="break"></div>
        </div>

        <!-- RETURN LABEL -->
        <h3 class="margin-bottom-s">Return Label:</h3>
        <div class="return-label-container" :class="{ 'return-label-container-safari': isSafari }">
          <img id="orReturns_labelImage" class="print-label-load-image" :src="labelImage" alt="Shipping Label" @load="imgLoad()" />
          <div class="reservation-barcode">
            <img class="print-label-load-image" :src="barCodeImage2" alt="Reservation Barcode" @load="imgLoad()" />
            <br />
            <div v-if="!isMarketplaceOrder">
              {{ 'Return ID #' }}
              {{ returnDetails.pendingReturnId }}
            </div>
            <div v-if="isMarketplaceOrder">
              Order #:
              {{ returnDetails.orderNumber }}
            </div>
          </div>
        </div>
      </div>

      <!-- BRAND LOGO 2ND PAGE-->
      <BrandLogo class-name="print-header print-only" />

      <div class="print-slip-header">
        <div class="return-slip">
          <h3>{{ ssreType ? 'Packing' : 'Return' }} Slip</h3>
        </div>
      </div>
    </div>

    <div class="return-confirmation-header">
      <div class="print-only margin-top-xxs">
        <div class="return-order-barcode">
          <img class="barcode-image print-label-load-image" alt="Barcode" :src="isPendingReturn ? barCodeImage : barCodeImage2" @load="imgLoad()" />
          <div class="pending-return-id">
            <div class="margin-bottom-xxs">
              {{ returnIdLabel }}
            </div>
            <div>
              {{ returnIdValue }}
            </div>
          </div>
        </div>
      </div>
      <div class="printlabel-return-details">
        <div v-if="showCustomForm">
          <a
            id="print-customs-form"
            href="https://cns.usps.com/cfo-web/labelInformation.shtml"
            class="button small submit expanded"
            target="_blank"
            rel="noopener"
          >
            PRINT CUSTOMS FORM
          </a>
        </div>
        <div class="confirmation-item">
          <div class="item-description">Return submitted on:</div>
          <div class="item-value js-return-submitted-date">
            {{ formatDate(returnDetails.returnSubmittedDate) }}
          </div>
        </div>
        <div v-if="!ssreType && estimatedCreditResponse && !estimatedCreditResponse.giftCardMessageFlag" class="confirmation-item">
          <div class="item-description">
            {{ estimatedCreditResponse.refundToMessage }}
          </div>
          <div v-if="estimatedCreditResponse.returnsRefundMethodEnabledFlag">
            <div v-if="estimatedCreditResponse.originalPaymentFlag" class="item-value">
              <div v-if="estimatedCreditResponse.refundToAccount?.toUpperCase() === 'KLARNA'">
                <img :src="klarnaImg" alt="KLARNA" class="margin-right-xxs klarna-icon" />
              </div>
              <div v-else>
                {{ estimatedCreditResponse.refundToAccount }}
              </div>
            </div>
            <div v-if="estimatedCreditResponse.refundToOriginalPaymentFlag" class="item-value">
              {{ estimatedCreditResponse.refundToOriginalPayment }}
            </div>
            <div v-if="estimatedCreditResponse.eGiftCardMessageFlag" class="item-value">
              {{ giftCardText }}
            </div>
          </div>
          <div v-else>
            <div v-if="reservationPaymentTypeListFlag" class="item-value">
              {{ estimatedCreditResponse.giftcardPaymentMesssage }}
            </div>
          </div>
        </div>
        <div v-if="!ssreType && estimatedCreditResponse.giftCardMessageFlag" class="grid-x mail-gift-card-to">
          <div class="item-description js-refund-to">Mail Gift Card To:</div>
          <div class="cell auto item-value mail-gift-card-address text-right">
            <div>
              {{ returnDetails.contact?.firstName }}
              {{ returnDetails.contact?.lastName }}
            </div>
            <div>
              {{ returnDetails.contact?.address?.addressLine1 }}
            </div>
            <div>
              {{ returnDetails.contact?.address?.addressLine2 }}
            </div>
            <div>
              {{ returnDetails.contact?.address?.addressLine3 }}
            </div>
            <div>
              {{ returnDetails.contact?.address?.city }}
              {{ returnDetails.contact?.address?.state }}
              {{ returnDetails.contact?.address?.postalCode }}
            </div>
          </div>
        </div>
        <div v-if="!ssreType && estimatedCreditResponse">
          <div v-if="estimatedCreditResponse.previousCreditFlag">
            <div class="confirmation-item js-estimated-credit">
              <div class="item-description">
                <strong>{{ estimatedCreditResponse.previousCreditMessage }}</strong>
              </div>
              <div id="om_returns_previous-credit" class="item-value js-estimated-credit">
                <strong>{{ formatPrice(estimatedCreditResponse.previousCreditAmount) }}</strong>
              </div>
            </div>
          </div>
          <div class="confirmation-item js-estimated-credit">
            <div class="item-description">
              <strong>{{ estimatedCreditResponse.estimatedCreditMessage }}</strong>
            </div>
            <div class="item-value js-estimated-credit">
              <strong id="om_returns_estimated-credit">{{ formatPrice(estimatedCreditResponse.estimatedCreditAmount) }}</strong>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="confirmation-item js-estimated-credit">
            <div class="item-description">
              <strong>Estimated amount due:</strong>
            </div>
            <div id="om_returns_previous-credit" class="item-value js-estimated-credit">
              <strong>{{ formatPrice(page === 'confirmation' ? returnDetails.estimatedAmountDue : returnDetails.estimatedRefund) }}</strong>
            </div>
          </div>
        </div>
        <div class="confirmation-item">
          <div class="item-description">Email:</div>
          <div class="item-value">
            {{ returnDetails.emailAddress }}
          </div>
        </div>
        <div v-if="isMarketplaceOrder" class="confirmation-item">
          <div class="item-description">Seller Order ID #:</div>
          <div class="item-value">
            {{ returnDetails.items[0]?.itemDetails?.mkpLineItemDetails?.partnerFulfillmentId }}
          </div>
        </div>
        <div v-if="isUPSReturnMethod" class="confirmation-item">
          <div class="item-description">UPS Tracking #:</div>
          <div class="item-value">
            {{ returnDetails.returnTrackingNumber }}
          </div>
        </div>
        <div v-if="isFedExReturnMethod" class="confirmation-item">
          <div class="item-description">FedEx Tracking #:</div>
          <div class="item-value">
            {{ returnDetails.returnTrackingNumber }}
          </div>
        </div>
        <div class="confirmation-item">
          <div class="item-description">Order #:</div>
          <div class="item-value js-confirmation-order-number">
            {{ returnDetails.orderNumber }}
          </div>
        </div>
        <div
          v-if="(isPendingReturn && !returnDetails.rmaId) || (!isPendingReturn && !returnDetails.rmaId && !isMarketplaceOrder)"
          class="confirmation-item"
        >
          <div class="item-description">Reservation #:</div>
          <div class="item-value js-confirmation-reservation-number">
            {{ padStart(returnDetails.reservationNumber) }}
          </div>
        </div>
      </div>
    </div>
    <!-- PRODUCT LIST -->
    <div class="confirmation-product-list">
      <div class="print-return-confirmation-product-list">
        <h3 v-if="ssreType" class="margin-bottom-xxs">Items you're returning</h3>
        <div v-for="item in returnDetails.items" :key="item.id" class="product-item">
          <div class="product-item-details">
            <div class="product-item-title">
              <span v-if="item?.itemDetails?.vendorName">{{ item.itemDetails.vendorName }},</span>
              {{ item?.itemDetails?.productName }}
            </div>

            <span v-if="item.itemDetails?.upcColor" class="details-color">Color: {{ item.itemDetails?.upcColor }}</span>

            <span v-if="item.itemDetails.upcSize" class="details-size">Size: {{ item.itemDetails.upcSize }}</span>
            <span v-if="item.upc" class="details-upc">UPC: {{ item.upc }}</span>
            <div v-if="isMarketplaceOrder">Shipped and sold by {{ item?.itemDetails?.mkpLineItemDetails?.sellerName }}</div>
          </div>
          <div class="product-item-price">
            <span v-if="item.returnQuantity" class="details-qty">Qty: {{ item.returnQuantity }}</span>

            <span v-if="!ssreType && item.currentPrice" class="details-price margin-left-xxs">Price: {{ formatPrice(item.currentPrice) }}</span>
            <div v-if="!ssreType" class="details-credit-subtotal">Credit Subtotal: {{ formatPrice(item?.itemTotal) }}</div>
          </div>
        </div>

        <template v-if="ssreType">
          <h3 class="margin-bottom-xxs">Items you're receiving</h3>
          <div v-for="item in returnDetails.substituteShipments[0].substituteItems" :key="item.id" class="product-item">
            <div class="product-item-details">
              <div class="product-item-title">
                <span v-if="item.itemDetails.vendorName">{{ item.itemDetails.vendorName }},</span>
                {{ item.itemDetails.productName }}
              </div>

              <span v-if="item.itemDetails.upcColor" class="details-color">Color: {{ item.itemDetails.upcColor }}</span>

              <span v-if="item.itemDetails.upcSize" class="details-size">Size: {{ item.itemDetails.upcSize }}</span>
              <span v-if="item.upc" class="details-upc">UPC: {{ item.upc }}</span>
              <div v-if="isMarketplaceOrder">Shipped and sold by {{ item?.itemDetails.mkpLineItemDetails.sellerName }}</div>
            </div>
            <div class="product-item-price">
              <span v-if="item.itemDetails.quantity" class="details-qty">Qty: {{ item.itemDetails.quantity }}</span>
            </div>
          </div>
          <div class="confirmation-return-details">
            <div class="js-estimated-credit">
              <div>
                <div>
                  <div class="confirmation-item js-estimated-credit">
                    <div class="item-description">
                      <strong>Estimated amount due:</strong>
                    </div>
                    <div class="item-value js-estimated-credit">
                      <strong
                        v-text="formatPrice(page === 'confirmation' ? returnDetails.estimatedAmountDue : returnDetails.estimatedRefund)"
                      ></strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-if="!ssreType" class="confirmation-return-details">
          <div class="confirmation-item">
            <div class="item-description">Merchandise Total:</div>
            <div class="item-value js-merchandiseTotal">
              {{ formatPrice(returnDetails.merchandiseTotal) }}
            </div>
          </div>
          <div v-if="returnDetails.protectionPlanTotal" class="confirmation-item">
            <div class="item-description">Allstate Protection Plan:</div>
            <div class="item-value js-tax">
              {{ formatPrice(returnDetails.protectionPlanTotal) }}
            </div>
          </div>
          <div class="confirmation-item">
            <div class="item-description" v-text="page === 'return-details' ? 'Tax Total:' : 'Estimated Tax:'"></div>
            <div class="item-value js-tax">
              {{ formatPrice(returnDetails.totalTaxes) }}
            </div>
          </div>
          <div v-if="eWasteFee" class="confirmation-item">
            <div class="item-description">eWaste Recycling Fee:</div>
            <div class="item-value js-tax">
              {{ formatPrice(eWasteFee) }}
            </div>
          </div>
          <div v-if="showShippingFee" class="confirmation-item">
            <div class="item-description">Estimated Return Shipping Fee:</div>
            <div class="item-value js-estimatedReturnShippingFee">
              {{ returnDetails.shippingFee === 0.0 ? config.FREE_SHIPMENT : `-${formatPrice(returnDetails.shippingFee)}` }}
            </div>
          </div>
          <div v-if="returnDetails.batteryFee" class="confirmation-item">
            <div class="item-description">CA embedded battery fee:</div>
            <div class="item-value js-tax">
              {{ formatPrice(returnDetails.batteryFee) }}
            </div>
          </div>
          <div class="js-estimated-credit">
            <div v-if="estimatedCreditResponse">
              <div v-if="estimatedCreditResponse.previousCreditFlag">
                <div class="confirmation-item js-estimated-credit">
                  <div class="item-description">
                    <strong>{{ estimatedCreditResponse.previousCreditMessage }}</strong>
                  </div>
                  <div class="item-value js-estimated-credit">
                    <strong>{{ formatPrice(estimatedCreditResponse.previousCreditAmount) }}</strong>
                  </div>
                </div>
              </div>
              <div v-else class="confirmation-item js-estimated-credit">
                <div class="item-description">
                  <strong>{{ estimatedCreditResponse.estimatedCreditMessage }}</strong>
                </div>
                <div class="item-value js-estimated-credit">
                  <strong id="om_returns_estimated-credit">{{ formatPrice(estimatedCreditResponse.estimatedCreditAmount) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isMarketplaceOrder" class="return-instructions">
      <div class="h4">Instructions for store colleagues only</div>
      <ol class="margin-top-xs margin-left-s">
        <li>Press T2 Returns/Exchange</li>
        <li>Scan or key the returns barcode on the packing slip</li>
        <li>Scan UPC(s) of item(s) being returned</li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import ReturnInstructions from '@/components/returns/ReturnInstructions.vue';
import BrandLogo from '@/components/BrandLogo.vue';

import { formatPrice } from '@/utils/returns/General';
import estimatedCredit from '@/utils/returns/EstimatedCredit';
import { padStart, formatDate, isSafariBrowser } from '@/utils/General';
import * as config from 'config';

import lithiumBatteryForbiddenImg from '@/assets/img/lithium-battery-forbidden.png';
import un3480Img from '@/assets/img/UN3480.jpg';
import un3481Img from '@/assets/img/UN3481.jpg';
import klarnaImg from '@core/csg/icons/mcom/compressed/third-party-payment-klarna.svg';

const props = defineProps({
  returnDetails: {
    type: Object,
    default: () => ({}),
  },
  ssreType: {
    type: String,
    default: '',
  },
  page: {
    type: String,
    default: 'confirmation',
  },
});

const emit = defineEmits(['print']);
const isSafari = ref(false);
const estimatedCreditResponse = ref(null);
const imgLoadedCount = ref(0);

const isPendingReturn = computed(() => !!props.returnDetails.pendingReturnId);
const suppressPrintLabel = computed(() => !props.returnDetails.labelImageUrl);
const labelImage = computed(() => props.returnDetails.labelImageUrl);
const isMarketplaceOrder = computed(() => props.returnDetails.isMkpOrder ?? false);
const barCodeImage = computed(
  () => `/api/ordermodsreturns/v1/returns/barcodegenerator?no=${props.returnDetails.pendingReturnId}&format=PRINT128&add_hex=false`,
);
const barCodeImage2 = computed(() => {
  const { orderNumber, pendingReturnId, rmaId } = props.returnDetails;
  let number;
  if (isMarketplaceOrder.value) {
    number = orderNumber;
  } else {
    number = isPendingReturn.value ? pendingReturnId : rmaId;
  }
  return `/api/ordermodsreturns/v1/returns/barcodegenerator?no=${number}&format=PRINT128&add_hex=false`;
});
const isUPSReturnMethod = computed(() => props.returnDetails.returnDropOffMethod === config.RETURN_METHOD_KEY_MAP.ups);
const isFedExReturnMethod = computed(() => props.returnDetails.returnDropOffMethod === config.RETURN_METHOD_KEY_MAP.fedex);
const hazmatItems = computed(() => {
  const items = props.returnDetails.items ?? [];
  return items.filter((item) => item?.isHazmatItem);
});
const hazmatLabel = computed(() => {
  const hasPackedItem = hazmatItems.value.some((item) => item.itemDetails.hazmatDescription === config.HAZMAT_INSTRUCTIONS.packedItem.key);
  const hasContainedItem = hazmatItems.value.some((item) => item.itemDetails.hazmatDescription === config.HAZMAT_INSTRUCTIONS.containedItem.key);
  const hasStandaloneItem = hazmatItems.value.some((item) => item.itemDetails.hazmatDescription === config.HAZMAT_INSTRUCTIONS.standaloneItem.key);

  return hasStandaloneItem && !hasContainedItem && !hasPackedItem ? 'UN3480' : 'UN3481';
});
const parentContainer = computed(() => document.getElementsByClassName('print-return-confirmation-container')[0]);
const eWasteFee = computed(
  () => config.brand === 'mcom' && isMarketplaceOrder.value && props.returnDetails.eWasteFee !== 0.0 && props.returnDetails.eWasteFee,
);
const giftCardText = computed(() =>
  estimatedCreditResponse.value.eGiftCardMessageFlag ? `${config.brandName} E-Gift Card` : `${config.brandName} Gift Card`,
);
const showShippingFee = computed(() => {
  const isMcomBrand = config.brand === 'mcom';
  const isLabelFreeOrUPSReturn = [config.RETURN_METHOD_KEY_MAP.labelFree, config.RETURN_METHOD_KEY_MAP.ups].includes(
    props.returnDetails.returnDropOffMethod,
  );
  return isMcomBrand && props.returnDetails.conditionalReturnFeeEnabled && !isMarketplaceOrder.value && isLabelFreeOrUPSReturn;
});
const showCustomForm = computed(() => {
  const { militaryReturnFlag, returnDropOffMethod } = props.returnDetails;
  return !suppressPrintLabel.value && militaryReturnFlag && returnDropOffMethod === config.RETURN_METHOD_KEY_MAP.ups;
});
const returnIdLabel = computed(() => {
  if (isPendingReturn.value) {
    return props.returnDetails.rmaId ? 'Return ID #' : 'Pending Return #';
  }
  return !isMarketplaceOrder.value ? 'Reservation Number #' : 'Order #';
});
const returnIdValue = computed(() => {
  if (isPendingReturn.value) {
    return props.returnDetails.pendingReturnId;
  }
  return !isMarketplaceOrder.value ? padStart(props.returnDetails.reservationNumber) : props.returnDetails.orderNumber;
});
const returnItems = computed(() =>
  props.returnDetails.items.map((item) => ({
    ...item,
    hazmatDescription: item.itemDetails?.hazmatDescription,
  })),
);
const hasPackedWithItem = computed(() =>
  hazmatItems.value.some((item) => item.itemDetails.hazmatDescription === config.HAZMAT_INSTRUCTIONS.packedItem.key),
);
const getHazmatImagePath = (label) => {
  return label === 'UN3480' ? un3480Img : un3481Img;
};
const imgLoad = () => {
  imgLoadedCount.value += 1;
  if (imgLoadedCount.value === parentContainer.value?.getElementsByClassName('print-label-load-image').length) {
    emit('print', true);
  }
};

onMounted(() => {
  isSafari.value = isSafariBrowser(navigator.userAgent);
  estimatedCreditResponse.value = estimatedCredit(props.returnDetails);
});
</script>

<style lang="scss" scoped>
@media print {
  .return-label-container-safari {
    padding: rem-calc(5 0) !important;
  }
  .hazmat-image-size-safari {
    height: rem-calc(240) !important;
  }
  .hazmat-packed-with-image-size-safari {
    height: rem-calc(190) !important;
  }
}
.print-return-confirmation-container {
  overflow: visible;
  margin: 0;
  background-color: $white;
  font-size: rem-calc(16);
  line-height: rem-calc(20);
  .print-header {
    width: rem-calc(300);
    margin: rem-calc(20 0 20 0);
    &.print-only {
      padding-top: rem-calc(16);
    }
  }

  .return-confirmation-mrl {
    ul {
      margin-left: rem-calc(45);
      list-style: decimal;
      margin-bottom: rem-calc(24);
    }
    li {
      line-height: rem-calc(24);
    }
    .h3 {
      margin-bottom: rem-calc(24);
    }
    .return-label-container {
      border-top: rem-calc(3) dashed $black;
      border-bottom: rem-calc(3) dashed $black;
      padding: rem-calc(60 0);
      position: relative;
      text-align: center;
      page-break-after: always;
      .reservation-barcode {
        transform: rotateZ(-90deg);
        position: absolute;
        right: rem-calc(-30);
        top: 50%;
        width: rem-calc(160);
        img {
          width: fit-content;
          height: rem-calc(40);
        }
      }
    }
  }

  .print-slip-header {
    display: flex;
    font-size: rem-calc(10);
    justify-content: space-between;
    line-height: normal;
    margin-bottom: rem-calc(16);
    border-bottom: rem-calc(0);
    padding: 0 1.25rem;

    &.empty {
      justify-content: flex-end;
      position: relative;
      top: rem-calc(-50);
      margin-bottom: rem-calc(-25);
    }
    .return-slip {
      flex-basis: 63%;
    }
  }

  .return-confirmation-header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0 rem-calc(18);

    .klarna-icon {
      display: inline;
    }

    .return-order-qrcode {
      font-weight: bold;
      flex-basis: 55%;
    }
    .printlabel-return-details {
      flex-basis: 10%;
      margin-bottom: rem-calc(10);
    }
    .barcode-image {
      height: rem-calc(40);
      width: rem-calc(200);
    }
    > div {
      flex: 1;
    }
  }

  .return-order-qrcode,
  .return-order-barcode {
    display: inline-block;
    font-weight: bold;
  }

  .qr-code-image,
  .barcode-image,
  .pending-return-id,
  .printlabel-return-details {
    text-align: center;
    margin-bottom: rem-calc(16);
  }

  .confirmation-item {
    display: flex;
    justify-content: space-between;
    line-height: rem-calc(24);

    &.estimated-credit {
      font-weight: bold;
    }
    .item-value {
      text-align: right;
      .klarna {
        background-position: rem-calc(4) 0;
      }
    }
  }
  .mail-gift-card-to {
    flex-direction: row;
  }

  .mail-gift-card-address {
    line-height: rem-calc(27);
  }

  #pick-dropoff {
    p:last-child {
      margin: 0;
    }
  }

  .military-dropoff {
    margin-top: rem-calc(40);
    ol {
      margin-left: rem-calc(20);
    }
  }
  .klarna-confirmation {
    height: rem-calc(14);
    width: rem-calc(54);
  }
  .return-instructions {
    flex-basis: 45%;
    .h4 {
      font-weight: 300;
    }
    ol {
      margin-left: rem-calc(11);
      font-size: rem-calc(16);
    }
  }
}

.split-label {
  border: 1px dashed $black;
  margin-bottom: rem-calc(16);
}

.hazmat-image {
  display: flex;
  height: rem-calc(340);
  .hazmat-label {
    margin-right: rem-calc(61);
  }
}
.break {
  break-before: page;
}

//product list
.confirmation-product-list {
  margin: rem-calc(64) auto rem-calc(32) auto;
  font-size: rem-calc(16);
  line-height: rem-calc(25);
  border-bottom: rem-calc(1) solid $gray-2-color;

  .print-return-confirmation-product-list {
    background: $white;
    padding: rem-calc(0 18 12 18);

    div {
      page-break-inside: avoid;
    }

    .confirmation-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: rem-calc(16);
    }

    .estimated-credit {
      font-weight: bold;
    }

    .product-item {
      border: rem-calc(1) solid $gray-2-color;
      line-height: rem-calc(25);
      margin-bottom: rem-calc(32);
      padding: rem-calc(20);

      .product-item-price {
        margin-bottom: rem-calc(5);
        text-align: right;
        white-space: nowrap;
        .details-credit-subtotal {
          font-weight: bold;
        }
      }
    }

    .confirmation-return-details {
      border: rem-calc(0) solid $gray-2-color;
      padding: rem-calc(0);
      margin-left: 56%;
      padding-top: rem-calc(16);
      padding-bottom: rem-calc(16);
    }
    .product-item {
      display: flex;
      border: rem-calc(0) solid $gray-2-color;
      line-height: rem-calc(25);
      margin-bottom: rem-calc(32);
      padding: rem-calc(0);

      .product-item-details {
        flex: 1;
        .details-color,
        .details-size {
          margin-right: rem-calc(18);
        }
        .details-reason-for-return {
          display: inline;
          font-size: rem-calc(11);
        }
      }
    }
  }
}
.hazmat-spacing {
  min-height: rem-calc(500);
}
</style>
